import {
    mdiAccountCashOutline,
    mdiAccountTie,
    mdiArrowDown,
    mdiArrowUp,
    mdiChevronDown,
    mdiChevronUp,
    mdiDelete,
    mdiDragVertical,
    mdiGavel,
    mdiMinusCircleOutline,
    mdiPlus,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { ISelectOption } from '../../base';
import { MESSAGE_CATEGORIES } from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    AutomationRule,
    AutomationWorker,
    RuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import { RULE_FIELD_HELPERS } from '../../../services/worker-configuration/automation/worker-automation.constants';
import { ReconsiderationDocument } from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    ENTER_TASK_FEE_DISCLAIMER,
    formatRuleMatchSummary,
    getActionMeta,
    getAvailableRuleActionTypes,
    isEnterTaskMessageOwner,
    resolveRuleActionType,
    RULE_ACTION_SELECT_LABELS,
} from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { ConfigurationActionSummary, ConfigurationFieldLabel } from './configuration-action-summary';
import { ConfigurationAssignedUsers } from './configuration-assigned-users';
import { ConfigurationReconsiderationDocuments } from './configuration-reconsideration-documents';
import { ConfigurationRuleMatchBlock } from './configuration-rule-match-block';
import * as S from './configuration-rule.styles';
import { SegmentButton, SegmentedControl } from '../detail/option-field.styles';

interface IConfigurationRuleCardProps {
    disabled?: boolean;
    index: number;
    rule: AutomationRule;
    store: WorkerStore;
    worker: AutomationWorker;
}

function getActionBadgeVariant(type: RuleActionType): 'attention' | 'neutral' | 'primary' | 'success' {
    switch (type) {
        case 'SWITCH_TO_PRIVATE_PAY':
            return 'attention';
        case 'CREATE_APPEAL':
            return 'primary';
        case 'CREATE_MESSAGE':
            return 'success';
        default:
            return 'neutral';
    }
}

function getActionIcon(type: RuleActionType): string {
    switch (type) {
        case 'SWITCH_TO_PRIVATE_PAY':
            return mdiAccountCashOutline;
        case 'CREATE_APPEAL':
            return mdiGavel;
        case 'CREATE_MESSAGE':
            return mdiAccountTie;
        default:
            return mdiMinusCircleOutline;
    }
}

export const ConfigurationRuleCard: React.FC<IConfigurationRuleCardProps> = observer(
    ({ disabled, index, rule, store, worker }) => {
        const expanded = store.expandedRuleId === rule.id;
        const isDragging = store.draggingRuleId === rule.id;
        const isDragOver =
            store.dragOverRuleId === rule.id && Boolean(store.draggingRuleId) && store.draggingRuleId !== rule.id;
        const actionMeta = getActionMeta(rule.action.type);
        const resolvedActionType = resolveRuleActionType(rule.action.type);
        const actionValue = rule.action.value;
        const availableScopes = store.getAvailableMatchScopesForWorker();
        const reconsiderationDocuments = (actionValue.documents as ReconsiderationDocument[] | undefined) ?? [];

        const actionSelectOptions = getAvailableRuleActionTypes(worker.id).map(value => ({
            label: RULE_ACTION_SELECT_LABELS[value],
            value,
        }));
        const selectedActionOption =
            actionSelectOptions.find(option => option.value === rule.action.type) ?? actionSelectOptions[0];
        const configuredCategory = String(actionValue.messageCategory ?? '').trim();
        const messageCategoryValues = Array.from(
            new Set(
                [...MESSAGE_CATEGORIES, configuredCategory]
                    .map(category => String(category).trim())
                    .filter(Boolean),
            ),
        );
        const messageCategoryOptions: ISelectOption<string>[] = messageCategoryValues.map(category => ({
            label: category,
            value: category,
        }));
        const configuredCategoryOrFallback = configuredCategory || 'PAYER_FOLLOWUP';
        const selectedMessageCategory =
            messageCategoryOptions.find(option => option.value === configuredCategoryOrFallback) ?? messageCategoryOptions[0];
        const isEnterTaskOwner = isEnterTaskMessageOwner(actionValue.messageOwner);

        return (
            <S.RuleCard dragging={isDragging} dragOver={isDragOver}>
                <S.RuleHeader
                    type="button"
                    data-allow-interaction="true"
                    onClick={() => store.toggleRuleExpanded(rule.id)}
                    draggable={!disabled}
                    onDragStart={event => {
                        if (disabled) {
                            return;
                        }
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', rule.id);
                        store.setDraggingRule(rule.id);
                    }}
                    onDragOver={event => {
                        event.preventDefault();
                        store.setDragOverRule(rule.id);
                    }}
                    onDrop={event => {
                        event.preventDefault();
                        store.reorderRule(rule.id);
                    }}
                    onDragEnd={() => store.setDraggingRule(null)}>
                    {!disabled && (
                        <S.DragHandle onClick={event => event.stopPropagation()}>
                            <Icon path={mdiDragVertical} size={0.85} aria-hidden={true} />
                        </S.DragHandle>
                    )}
                    <S.PriorityBadge>{index + 1}</S.PriorityBadge>
                    <S.RuleSummary>
                        <S.RuleName>{rule.name}</S.RuleName>
                        <S.RuleMatchSummary>
                            {formatRuleMatchSummary(
                                rule.match,
                                store.catalog,
                                store.payerById,
                                store.providerById,
                                store.facilityById,
                            )}
                        </S.RuleMatchSummary>
                    </S.RuleSummary>
                    <S.ActionBadge variant={getActionBadgeVariant(rule.action.type)}>
                        <Icon path={getActionIcon(rule.action.type)} size={0.65} aria-hidden={true} />
                        {actionMeta.label}
                    </S.ActionBadge>
                    <Icon
                        path={expanded ? mdiChevronUp : mdiChevronDown}
                        size={0.9}
                        color="#8e9293"
                        aria-hidden={true}
                    />
                </S.RuleHeader>

                {expanded && (
                    <S.RuleBody data-readonly={disabled ? 'true' : undefined}>
                        <div style={{ marginBottom: 18 }}>
                            <S.FieldLabel>Rule name</S.FieldLabel>
                            <S.FieldInput
                                value={rule.name}
                                disabled={disabled}
                                readOnly={disabled}
                                onChange={event => {
                                    if (!disabled) {
                                        store.renameRule(rule.id, event.target.value);
                                    }
                                }}
                            />
                        </div>

                        <S.SectionLabel>
                            <S.WhenTag>WHEN</S.WhenTag>
                            <S.SectionHint>all match blocks must match (AND)</S.SectionHint>
                        </S.SectionLabel>

                        <S.MatchBlockStack>
                            {rule.match.map((match, matchIndex) => (
                                <ConfigurationRuleMatchBlock
                                    key={`${rule.id}-${matchIndex}-${match.scope}`}
                                    rule={rule}
                                    match={match}
                                    matchIndex={matchIndex}
                                    removable={!disabled && rule.match.length > 1}
                                    availableScopes={availableScopes}
                                    store={store}
                                    disabled={disabled}
                                />
                            ))}
                        </S.MatchBlockStack>

                        {!disabled && (
                            <S.AddMatchButton
                                type="button"
                                disabled={disabled}
                                onClick={() => store.addMatchBlock(rule.id)}>
                                <Icon path={mdiPlus} size={0.75} aria-hidden={true} />
                                Add condition (AND)
                            </S.AddMatchButton>
                        )}

                        <S.SectionLabel>
                            <S.ThenTag>THEN</S.ThenTag>
                            <S.SectionHint>route to action bucket</S.SectionHint>
                        </S.SectionLabel>

                        <S.ActionPanel>
                            <S.ActionRow>
                                <S.FieldLabel style={{ marginBottom: 0, flex: 'none' }}>Action</S.FieldLabel>
                                <S.FieldSelect
                                    options={actionSelectOptions}
                                    value={selectedActionOption}
                                    required
                                    disabled={disabled}
                                    onChange={selected => {
                                        const option = selected as ISelectOption<string> | null;
                                        if (option?.value) {
                                            store.changeRuleAction(rule.id, option.value as RuleActionType);
                                        }
                                    }}
                                />
                            </S.ActionRow>

                            <ConfigurationActionSummary actionType={rule.action.type} />

                            {resolvedActionType === 'CLOSE_OUT' && (
                                <div>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.closeoutDescription}>
                                        Closeout reason
                                    </ConfigurationFieldLabel>
                                    <S.FieldInput
                                        type="text"
                                        maxLength={45}
                                        disabled={disabled}
                                        readOnly={disabled}
                                        value={String(actionValue.closeoutDescription ?? '')}
                                        onChange={event =>
                                            store.setRuleActionValue(
                                                rule.id,
                                                'closeoutDescription',
                                                event.target.value.slice(0, 45),
                                            )
                                        }
                                    />
                                </div>
                            )}

                            {resolvedActionType === 'SWITCH_TO_PRIVATE_PAY' && (
                                <div>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.memo} optional>
                                        Invoice memo
                                    </ConfigurationFieldLabel>
                                    <S.FieldTextarea
                                        rows={3}
                                        disabled={disabled}
                                        value={String(actionValue.memo ?? '')}
                                        onChange={event =>
                                            store.setRuleActionValue(rule.id, 'memo', event.target.value)
                                        }
                                    />
                                </div>
                            )}

                            {resolvedActionType === 'CREATE_APPEAL' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.prompt} optional>
                                            Customize the cover letter
                                        </ConfigurationFieldLabel>
                                        <S.FieldTextarea
                                            rows={3}
                                            disabled={disabled}
                                            value={String(actionValue.prompt ?? '')}
                                            onChange={event =>
                                                store.setRuleActionValue(rule.id, 'prompt', event.target.value)
                                            }
                                        />
                                    </div>

                                    <ConfigurationReconsiderationDocuments
                                        ruleId={rule.id}
                                        documents={reconsiderationDocuments}
                                        store={store}
                                        disabled={disabled}
                                    />
                                </div>
                            )}

                            {resolvedActionType === 'CREATE_MESSAGE' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageOwner}>
                                            Responsibility owner
                                        </ConfigurationFieldLabel>
                                        <SegmentedControl aria-disabled={disabled}>
                                            <SegmentButton
                                                type="button"
                                                active={actionValue.messageOwner !== 'PROVIDER'}
                                                disabled={disabled}
                                                onClick={() => {
                                                    if (!disabled) {
                                                        store.setRuleActionValue(rule.id, 'messageOwner', 'ENTER');
                                                    }
                                                }}>
                                                ENTER
                                            </SegmentButton>
                                            <SegmentButton
                                                type="button"
                                                variant="neutral"
                                                active={actionValue.messageOwner === 'PROVIDER'}
                                                disabled={disabled}
                                                onClick={() => {
                                                    if (!disabled) {
                                                        store.setRuleActionValue(rule.id, 'messageOwner', 'PROVIDER');
                                                    }
                                                }}>
                                                PROVIDER
                                            </SegmentButton>
                                        </SegmentedControl>
                                    </div>

                                    <S.TwoColumnFields>
                                        <div>
                                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageCategory}>
                                                Category
                                            </ConfigurationFieldLabel>
                                            <S.FieldSelect
                                                isCreatable="true"
                                                options={messageCategoryOptions}
                                                value={selectedMessageCategory}
                                                required
                                                disabled={disabled}
                                                onCreateOption={(value: string) => {
                                                    const nextCategory = value.trim();
                                                    if (!nextCategory || disabled) {
                                                        return;
                                                    }
                                                    store.setRuleActionValue(rule.id, 'messageCategory', nextCategory);
                                                }}
                                                onChange={selected => {
                                                    const option = selected as ISelectOption<string> | null;
                                                    if (option?.value) {
                                                        store.setRuleActionValue(rule.id, 'messageCategory', option.value);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.assigned}>
                                                Assigned
                                            </ConfigurationFieldLabel>
                                            <ConfigurationAssignedUsers
                                                disabled={disabled}
                                                loading={store.assignableUsersLoading}
                                                assignedBankUserIds={
                                                    (actionValue.assigned as string[] | undefined) ?? []
                                                }
                                                users={store.assignableUsers}
                                                onChange={bankUserIds =>
                                                    store.setAssignedUsers(rule.id, bankUserIds)
                                                }
                                            />
                                        </div>
                                    </S.TwoColumnFields>

                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.suggestedResolution}>
                                            Suggested next steps
                                        </ConfigurationFieldLabel>
                                        <S.FieldTextarea
                                            rows={2}
                                            disabled={disabled}
                                            readOnly={disabled}
                                            value={String(actionValue.suggestedResolution ?? '')}
                                            onChange={event =>
                                                store.setRuleActionValue(
                                                    rule.id,
                                                    'suggestedResolution',
                                                    event.target.value,
                                                )
                                            }
                                        />
                                        {isEnterTaskOwner && (
                                            <S.FieldHelper style={{ marginTop: 6, marginBottom: 0 }}>
                                                {ENTER_TASK_FEE_DISCLAIMER}
                                            </S.FieldHelper>
                                        )}
                                    </div>
                                </div>
                            )}
                        </S.ActionPanel>

                        {!disabled && (
                            <S.RuleFooter>
                                <S.FooterButton
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => store.moveRule(rule.id, -1)}>
                                    <Icon path={mdiArrowUp} size={0.65} aria-hidden={true} />
                                    Up
                                </S.FooterButton>
                                <S.FooterButton
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => store.moveRule(rule.id, 1)}>
                                    <Icon path={mdiArrowDown} size={0.65} aria-hidden={true} />
                                    Down
                                </S.FooterButton>
                                <S.FooterSpacer />
                                <S.FooterButton
                                    type="button"
                                    danger
                                    disabled={disabled}
                                    onClick={() => store.deleteRule(rule.id)}>
                                    <Icon path={mdiDelete} size={0.65} aria-hidden={true} />
                                    Delete rule
                                </S.FooterButton>
                            </S.RuleFooter>
                        )}
                    </S.RuleBody>
                )}
            </S.RuleCard>
        );
    },
);
